import { useCallback, useEffect, useRef, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Graticule,
  Sphere,
} from "react-simple-maps";
import countries from "i18n-iso-countries";
import ptLocale from "i18n-iso-countries/langs/pt.json";

countries.registerLocale(ptLocale);

const GEO_URL = "https://unpkg.com/world-atlas@2/countries-110m.json";

// Mesma rampa sequencial do mapa do admin: passo mais claro perto de zero,
// mais escuro = mais envios.
const RAMPA_AZUL = [
  "#cde2fb", "#b7d3f6", "#9ec5f4", "#86b6ef",
  "#6da7ec", "#5598e7", "#3987e5", "#2a78d6",
  "#256abf", "#1c5cab", "#184f95", "#104281", "#0d366b",
];
const SEM_DADO = "#e2e8f0";

/** Graus de rotação por pixel arrastado. */
const SENSIBILIDADE = 0.28;
/** Giro lento quando ninguém está mexendo, em graus por segundo. */
const GIRO_OCIOSO = 3;

export type DestinoVolume = { pais: string; total: number };

type Props = {
  data: DestinoVolume[];
  /** Altura do SVG. O globo se ajusta dentro dela. */
  altura?: number;
};

/**
 * Globo de destinos: arraste para girar 360°, países pintados por volume.
 *
 * É SVG com projeção ortográfica do d3-geo — não precisa de WebGL nem de
 * biblioteca nova, o react-simple-maps já estava no projeto para o mapa plano
 * do admin.
 *
 * Gira sozinho devagar quando ninguém interage, e para assim que o mouse
 * encosta, para dar tempo de ler o país.
 */
const GlobeDestinos = ({ data, altura = 380 }: Props) => {
  const [rotacao, setRotacao] = useState<[number, number]>([-50, -10]);
  const [arrastando, setArrastando] = useState(false);
  const [pausado, setPausado] = useState(false);
  const [alvo, setAlvo] = useState<{ nome: string; total: number } | null>(null);

  const ultimoPonto = useRef<{ x: number; y: number } | null>(null);

  // Índice por id numérico do world-atlas, que é o que o geo traz.
  const porId = new Map<string, { alpha2: string; total: number }>();
  let maximo = 0;
  for (const d of data) {
    const numerico = countries.alpha2ToNumeric(d.pais);
    if (!numerico) continue;
    porId.set(numerico, { alpha2: d.pais, total: d.total });
    if (d.total > maximo) maximo = d.total;
  }

  const corDe = (id: string) => {
    const item = porId.get(id);
    if (!item || maximo === 0) return SEM_DADO;
    // Raiz quadrada comprime o topo: sem isso um país dominante deixa todo o
    // resto no tom mais claro, e o mapa não informa nada.
    const posicao = Math.sqrt(item.total / maximo);
    const indice = Math.min(RAMPA_AZUL.length - 1, Math.floor(posicao * RAMPA_AZUL.length));
    return RAMPA_AZUL[indice];
  };

  // Giro ocioso. Some com prefers-reduced-motion e enquanto o mouse está no
  // globo ou arrastando.
  useEffect(() => {
    const semMovimento = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (semMovimento || arrastando || pausado) return;

    let frame = 0;
    let anterior = performance.now();

    const passo = (agora: number) => {
      const delta = (agora - anterior) / 1000;
      anterior = agora;
      setRotacao(([lambda, phi]) => [lambda + GIRO_OCIOSO * delta, phi]);
      frame = requestAnimationFrame(passo);
    };

    frame = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(frame);
  }, [arrastando, pausado]);

  const aoMover = useCallback(
    (e: React.PointerEvent) => {
      if (!ultimoPonto.current) return;

      const dx = e.clientX - ultimoPonto.current.x;
      const dy = e.clientY - ultimoPonto.current.y;
      ultimoPonto.current = { x: e.clientX, y: e.clientY };

      setRotacao(([lambda, phi]) => [
        lambda + dx * SENSIBILIDADE,
        // Trava nos polos: passar de 90° vira o globo de cabeça para baixo.
        Math.max(-90, Math.min(90, phi - dy * SENSIBILIDADE)),
      ]);
    },
    [],
  );

  return (
    <div
      className="relative select-none"
      onPointerEnter={() => setPausado(true)}
      onPointerLeave={() => {
        setPausado(false);
        setAlvo(null);
      }}
    >
      <div
        className={arrastando ? "cursor-grabbing" : "cursor-grab"}
        onPointerDown={(e) => {
          (e.target as Element).setPointerCapture?.(e.pointerId);
          ultimoPonto.current = { x: e.clientX, y: e.clientY };
          setArrastando(true);
        }}
        onPointerMove={(e) => arrastando && aoMover(e)}
        onPointerUp={() => {
          ultimoPonto.current = null;
          setArrastando(false);
        }}
        onPointerCancel={() => {
          ultimoPonto.current = null;
          setArrastando(false);
        }}
      >
        <ComposableMap
          projection="geoOrthographic"
          projectionConfig={{ rotate: [rotacao[0], rotacao[1], 0], scale: altura / 2.2 }}
          width={altura}
          height={altura}
          style={{ width: "100%", height: altura }}
        >
          <Sphere id="oceano" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth={0.6} />
          <Graticule stroke="#e2e8f0" strokeWidth={0.4} />
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const id = String(geo.id);
                const item = porId.get(id);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={corDe(id)}
                    stroke="#ffffff"
                    strokeWidth={0.3}
                    onMouseEnter={() =>
                      setAlvo(
                        item
                          ? {
                              nome:
                                countries.getName(item.alpha2, "pt") ?? item.alpha2,
                              total: item.total,
                            }
                          : null,
                      )
                    }
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", filter: item ? "brightness(1.12)" : "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {/* Legenda do que o mouse está tocando. Posição fixa no rodapé em vez de
          seguir o cursor: tooltip que persegue o mouse atrapalha quem arrasta. */}
      <div className="mt-1 flex h-6 items-center justify-center text-xs">
        {alvo ? (
          <span className="text-brand-text">
            <strong>{alvo.nome}</strong> · {alvo.total}{" "}
            {alvo.total === 1 ? "envio" : "envios"}
          </span>
        ) : (
          <span className="text-brand-text/40">Arraste para girar</span>
        )}
      </div>
    </div>
  );
};

export default GlobeDestinos;
